import { atom } from 'jotai';

import { purchaseLoadUpdateAtom } from '../_graphql/mutationPurchaseLoadLineItemsUpdate';

import { createAutoSaveFormAtoms } from './createAutoSaveFormAtoms.state';
import {
  $inboundTicketLoads,
  $scaleTicketDetail,
  $scaleTicketId,
} from './scaleTicketPro.state';

export type TicketSupplierFormState = {
  facilityId: string | null;
  supplierAccountId: string | null;
  supplierAddressId: string | null;
  purchaseOrderId: string | null;
  serviceId: string | null;
  carrierAccountId: string | null;
  freightTerms: string | null;
  shippingCarrierName: string | null;
  scac: string | null;
};

export const DEFAULT_SUPPLIER_FORM: TicketSupplierFormState = {
  facilityId: null,
  supplierAccountId: null,
  supplierAddressId: null,
  purchaseOrderId: null,
  serviceId: null,
  carrierAccountId: null,
  freightTerms: null,
  shippingCarrierName: null,
  scac: null,
};

type TicketSupplierServerData = {
  facilityId: string | null;
  supplierAccountId: string | null;
  supplierAddressId: string | null;
  purchaseOrderId: string | null;
  serviceId: string | null;
  carrierAccountId: string | null;
  freightTerms: string | null;
  shippingCarrierName: string | null;
  scac: string | null;
};

const atoms = createAutoSaveFormAtoms<
  TicketSupplierFormState,
  TicketSupplierServerData
>({
  defaultForm: DEFAULT_SUPPLIER_FORM,
  fromServerData: (ticket) => ({
    facilityId: ticket.facilityId,
    supplierAccountId: ticket.supplierAccountId,
    supplierAddressId: ticket.supplierAddressId,
    purchaseOrderId: ticket.purchaseOrderId,
    serviceId: ticket.serviceId,
    carrierAccountId: ticket.carrierAccountId,
    freightTerms: ticket.freightTerms,
    shippingCarrierName: ticket.shippingCarrierName,
    scac: ticket.scac,
  }),
  createMutation: (onSuccess, onError) => {
    const $mut = purchaseLoadUpdateAtom({
      onSuccess: (get, set, { params }) => onSuccess(get, set, params.id),
      onError: (get, set, { params }) => onError(get, set, params.id),
    });
    return atom(
      null,
      (
        _get,
        set,
        params: { ticketId: string; form: TicketSupplierFormState },
      ) => {
        set($mut, { id: params.ticketId, ...params.form });
      },
    );
  },
  refetchOnSuccess: (get, ticketId) => {
    if (get($scaleTicketId) === ticketId) {
      get($scaleTicketDetail).refetch();
    }
    get($inboundTicketLoads).refetch();
  },
});

export const $ticketSupplierFormById = atoms.$formById;
export const $ticketSupplierBaselineById = atoms.$baselineById;
export const $updateTicketSupplierTextField = atoms.$updateField;
export const $syncTicketSupplierForm = atoms.$syncForm;
export const $flushTicketSupplierAutoSave = atoms.$flushAutoSave;
export const $cancelTicketSupplierAutoSave = atoms.$cancelAutoSave;

export const $cleanupTicketSupplierForm = atoms.$cleanupForm;

export const $updateTicketSupplierSelectField = atom(
  null,
  (
    _get,
    set,
    params: {
      field: keyof TicketSupplierFormState;
      value: string | null;
      ticketId: string;
    },
  ) => {
    set(atoms.$updateImmediately, {
      ticketId: params.ticketId,
      updates: {
        [params.field]: params.value,
      } as Partial<TicketSupplierFormState>,
    });
  },
);

export const $updateTicketSupplierSelectFields = atom(
  null,
  (
    _get,
    set,
    params: {
      updates: Partial<TicketSupplierFormState>;
      ticketId: string;
    },
  ) => {
    set(atoms.$updateImmediately, {
      ticketId: params.ticketId,
      updates: params.updates,
    });
  },
);
